import './pedido-detalhe-painel.js';
import type {
  PedidoListagem,
  StatusPedido,
  GatewayPagamento,
  OrdenacaoPedido,
  FunilProducao,
} from '@pgcustom/shared-types';
import { ROTULO_ORIGEM_PEDIDO, rotuloOrigemPedido, rotuloReferenciaPedido, ETAPA_CONCLUSAO } from '@pgcustom/shared-types';
import { formatBRL, centavosParaReais } from '@pgcustom/shared-utils';
import type { Resultado } from '../../core/resultado.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { PedidosApi, FiltroListaPedidos } from './pedidos.api.js';
import type { ClientesApi } from '../clientes/clientes.api.js';
import type { DashboardApi } from '../dashboard/dashboard.api.js';
import type { RelatoriosApi } from '../relatorios/relatorios.api.js';
import type { OpsApi } from '../producao/ops.api.js';
import type { ComissoesApi } from '../comissoes/comissoes.api.js';
import type { UsuariosApi } from '../usuarios/usuarios.api.js';
import type { PermissoesPedidos } from './pedidos.permissoes.js';
import { rotuloStatusPedido, STATUS_PEDIDO } from './status-pedido.js';
import type { ErpPedidoDetalhePainel } from './pedido-detalhe-painel.js';

const LIMITE_PAGINA = 20;

const ROTULO_GATEWAY: Record<GatewayPagamento, string> = {
  mercado_pago: 'Mercado Pago',
  infinitepay: 'Infinity Pay',
  stone: 'Stone',
  manual: 'Manual',
};

const TOM_STATUS_PEDIDO: Record<StatusPedido, 'pago' | 'pendente' | 'cancelado'> = {
  pago: 'pago',
  pendente: 'pendente',
  cancelado: 'cancelado',
};

interface FiltrosUi {
  status: StatusPedido | '';
  busca: string;
  periodo: '' | '30d' | 'mes' | '6m';
  gateway: GatewayPagamento | '';
  origem: string;
  ordenar: OrdenacaoPedido;
}

/** Amostra usada para os cards de "campanha"/"cupom" e a inteligência lateral — a API não expõe contagem total, mesma convenção do resto do ERP. */
interface Resumo {
  totalPorStatus: Record<StatusPedido, number> | null;
  totalPedidos: number | null;
  aproximado: boolean;
  faturamentoPagoCentavos: number | null;
  ticketMedioCentavos: number | null;
  pedidosPagosMes: number | null;
  emProducao: number | null;
  aguardandoEnvio: number | null;
  pedidosCampanha: number | null;
  pedidosComCupom: number | null;
  amostra: number;
  aguardandoAcao: number | null;
}

const RESUMO_VAZIO: Resumo = {
  totalPorStatus: null,
  totalPedidos: null,
  aproximado: false,
  faturamentoPagoCentavos: null,
  ticketMedioCentavos: null,
  pedidosPagosMes: null,
  emProducao: null,
  aguardandoEnvio: null,
  pedidosCampanha: null,
  pedidosComCupom: null,
  amostra: 0,
  aguardandoAcao: null,
};

/**
 * `<erp-pedidos-page>` — versão definitiva do módulo de Pedidos, reconstruída sobre a
 * referência visual `C:\Users\PG\Downloads\PEDIDOS\pg-custom-pedidos-top.html` (KPIs +
 * toolbar + abas de status + tabela densa + painel lateral "Inteligência dos pedidos"),
 * usando os tokens do Design System do ERP (dourado/rosa/roxo). Todo dado é real:
 * `PedidosApi.listar` (agora com busca/gateway/origem/período/ordenação server-side e cliente/
 * cupom/parceiro/comissão resolvidos numa query — `PedidoListagem`), `RelatoriosApi` (pulso do
 * negócio / funil de produção) e `DashboardApi` (distribuição por status). Nenhum mock.
 *
 * Abrir um pedido troca a listagem pelo `<erp-pedido-detalhe-painel>` (detalhe completo já
 * existente — produtos, valores, previsão, endereço, frete, documentos, timeline, envio,
 * rastreamento). Autorização é sempre do backend; `permissoes.ler` só decide o estado da tela.
 */
export class ErpPedidosPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _pedidosApi: PedidosApi | null = null;
  private _clientesApi: ClientesApi | null = null;
  private _dashboardApi: DashboardApi | null = null;
  private _relatoriosApi: RelatoriosApi | null = null;
  private _opsApi: OpsApi | null = null;
  private _comissoesApi: ComissoesApi | null = null;
  private _usuariosApi: UsuariosApi | null = null;
  private _permissoes: PermissoesPedidos = { ler: false, criar: false, atualizar: false };
  private _permissoesRelatorios = false;

  private filtros: FiltrosUi = { status: '', busca: '', periodo: '', gateway: '', origem: '', ordenar: 'recentes' };
  private offset = 0;
  private resultado: Resultado<PedidoListagem[]> = { status: 'carregando' };
  private itensAtuais: PedidoListagem[] = [];
  private resumo: Resumo = RESUMO_VAZIO;
  private buscaDebounce: number | null = null;

  private pedidoAbertoId: string | null = null;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set pedidosApi(valor: PedidosApi) {
    this._pedidosApi = valor;
    if (this.isConnected && !this.pedidoAbertoId) void this.carregar();
  }
  set clientesApi(valor: ClientesApi) {
    this._clientesApi = valor;
  }
  set opsApi(valor: OpsApi) {
    this._opsApi = valor;
  }
  set comissoesApi(valor: ComissoesApi) {
    this._comissoesApi = valor;
  }
  set usuariosApi(valor: UsuariosApi) {
    this._usuariosApi = valor;
  }
  set dashboardApi(valor: DashboardApi) {
    this._dashboardApi = valor;
    if (this.isConnected && !this.pedidoAbertoId) void this.carregarResumo();
  }
  set relatoriosApi(valor: RelatoriosApi) {
    this._relatoriosApi = valor;
    if (this.isConnected && !this.pedidoAbertoId) void this.carregarResumo();
  }
  set permissoesRelatorios(valor: { ler: boolean }) {
    this._permissoesRelatorios = valor.ler;
  }
  set permissoes(valor: PermissoesPedidos) {
    this._permissoes = valor;
    if (this.isConnected) this.render();
  }

  connectedCallback(): void {
    this.render();
    if (!this.pedidoAbertoId) {
      if (this._pedidosApi) void this.carregar();
      if (this._dashboardApi || this._relatoriosApi) void this.carregarResumo();
    }
  }

  private abrirDetalhe(pedidoId: string): void {
    this.pedidoAbertoId = pedidoId;
    this.render();
  }

  private fecharDetalhe(): void {
    this.pedidoAbertoId = null;
    this.render();
    if (this._pedidosApi) void this.carregar();
    if (this._dashboardApi || this._relatoriosApi) void this.carregarResumo();
  }

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  private render(): void {
    if (this.pedidoAbertoId) {
      this.renderPainelDetalhe(this.pedidoAbertoId);
      return;
    }
    this.root.innerHTML = `
      <style>${ESTILO}${FORM_FIELD_CSS}</style>
      <div class="wrap">
        <div class="eyebrow">Painel administrativo · pedidos</div>
        <h1>Pedidos</h1>
        <p class="sub">Controle comercial completo — pagamento, origem, cupom, produção e entrega.</p>

        <div class="kpis" id="kpis">${this.kpisHtml()}</div>

        <div class="toolbar">
          <input id="busca" class="field" placeholder="⌕ Buscar por NF, pedido, cliente, e-mail ou cupom" value="${esc(this.filtros.busca)}" />
          <select id="periodo" class="field">
            <option value="">Todos os períodos</option>
            <option value="mes">Este mês</option>
            <option value="30d">Últimos 30 dias</option>
            <option value="6m">Últimos 6 meses</option>
          </select>
          <select id="gateway" class="field">
            <option value="">Todos os gateways</option>
            ${(Object.keys(ROTULO_GATEWAY) as GatewayPagamento[])
              .map((g) => `<option value="${g}">${esc(ROTULO_GATEWAY[g])}</option>`)
              .join('')}
          </select>
          <select id="origem" class="field">
            <option value="">Todas as origens</option>
            ${Object.entries(ROTULO_ORIGEM_PEDIDO)
              .map(([codigo, rotulo]) => `<option value="${codigo}">${esc(rotulo)}</option>`)
              .join('')}
          </select>
          <select id="ordenar" class="field">
            <option value="recentes">Mais recentes</option>
            <option value="maior_valor">Maior valor</option>
            <option value="menor_valor">Menor valor</option>
          </select>
        </div>

        <div class="tabs" id="tabs">${this.tabsHtml()}</div>

        <div class="layout">
          <div class="tablebox">
            <div class="tablehead">
              <div>
                <strong>Lista de pedidos</strong>
                <div class="small">Fonte de verdade comercial · clique para abrir o pedido</div>
              </div>
            </div>
            <div class="tablewrap" id="tabela"></div>
            <div class="footer">
              <span id="info-pagina" class="small"></span>
              <div class="pager">
                <button id="anterior" class="pgbtn" type="button">Anterior</button>
                <button id="proxima" class="pgbtn" type="button">Próxima</button>
              </div>
            </div>
          </div>

          <aside class="sidebox" id="sidebox">${this.sideboxHtml()}</aside>
        </div>
      </div>
    `;

    this.sincronizarSelects();
    this.ligarEventos();
    this.atualizarTabela();
  }

  private renderPainelDetalhe(pedidoId: string): void {
    this.root.innerHTML = `
      <style>:host { display: block; font-family: var(--pg-font-familia-base, system-ui); }</style>
      <erp-pedido-detalhe-painel id="painel-detalhe"></erp-pedido-detalhe-painel>
    `;
    const painel = this.root.querySelector('#painel-detalhe') as ErpPedidoDetalhePainel;
    if (this._pedidosApi) painel.pedidosApi = this._pedidosApi;
    if (this._clientesApi) painel.clientesApi = this._clientesApi;
    if (this._opsApi) painel.opsApi = this._opsApi;
    if (this._comissoesApi) painel.comissoesApi = this._comissoesApi;
    if (this._usuariosApi) painel.usuariosApi = this._usuariosApi;
    painel.permissoes = this._permissoes;
    painel.aoVoltar = () => this.fecharDetalhe();
    painel.aoAtualizar = () => {
      /* a lista é recarregada ao voltar */
    };
    painel.abrirParaVer(pedidoId);
  }

  private sincronizarSelects(): void {
    const set = (id: string, valor: string) => {
      const el = this.root.querySelector<HTMLSelectElement>(`#${id}`);
      if (el) el.value = valor;
    };
    set('periodo', this.filtros.periodo);
    set('gateway', this.filtros.gateway);
    set('origem', this.filtros.origem);
    set('ordenar', this.filtros.ordenar);
  }

  private ligarEventos(): void {
    const busca = this.root.querySelector<HTMLInputElement>('#busca');
    busca?.addEventListener('input', () => {
      this.filtros.busca = busca.value;
      if (this.buscaDebounce) window.clearTimeout(this.buscaDebounce);
      this.buscaDebounce = window.setTimeout(() => {
        this.offset = 0;
        void this.carregar();
      }, 350);
    });

    const selecionar = (id: string, aplicar: (valor: string) => void) => {
      const el = this.root.querySelector<HTMLSelectElement>(`#${id}`);
      el?.addEventListener('change', () => {
        aplicar(el.value);
        this.offset = 0;
        void this.carregar();
      });
    };
    selecionar('periodo', (v) => (this.filtros.periodo = v as FiltrosUi['periodo']));
    selecionar('gateway', (v) => (this.filtros.gateway = v as GatewayPagamento | ''));
    selecionar('origem', (v) => (this.filtros.origem = v));
    selecionar('ordenar', (v) => (this.filtros.ordenar = (v || 'recentes') as OrdenacaoPedido));

    this.root.querySelectorAll<HTMLButtonElement>('.tab').forEach((tab) => {
      tab.addEventListener('click', () => {
        this.filtros.status = (tab.dataset['status'] ?? '') as StatusPedido | '';
        this.offset = 0;
        void this.carregar();
      });
    });

    this.root.querySelector('#anterior')?.addEventListener('click', () => {
      if (this.offset === 0) return;
      this.offset = Math.max(0, this.offset - LIMITE_PAGINA);
      void this.carregar();
    });
    this.root.querySelector('#proxima')?.addEventListener('click', () => {
      if (this.itensAtuais.length < LIMITE_PAGINA) return;
      this.offset += LIMITE_PAGINA;
      void this.carregar();
    });
  }

  // ---------------------------------------------------------------------
  // Dados
  // ---------------------------------------------------------------------

  private filtroApi(): FiltroListaPedidos {
    const f: FiltroListaPedidos = { limit: LIMITE_PAGINA, offset: this.offset, ordenar: this.filtros.ordenar };
    if (this.filtros.status) f.status = this.filtros.status;
    if (this.filtros.busca.trim()) f.busca = this.filtros.busca.trim();
    if (this.filtros.gateway) f.gateway = this.filtros.gateway;
    if (this.filtros.origem) f.origem = this.filtros.origem;
    const desde = this.periodoParaIso();
    if (desde) f.criadoDesde = desde;
    return f;
  }

  private periodoParaIso(): string | null {
    const agora = new Date();
    if (this.filtros.periodo === '30d') return new Date(agora.getTime() - 30 * 864e5).toISOString();
    if (this.filtros.periodo === '6m') return new Date(agora.getTime() - 182 * 864e5).toISOString();
    if (this.filtros.periodo === 'mes') return new Date(agora.getFullYear(), agora.getMonth(), 1).toISOString();
    return null;
  }

  private async carregar(): Promise<void> {
    const api = this._pedidosApi;
    if (!api) return;

    if (!this._permissoes.ler) {
      this.resultado = { status: 'sem-permissao' };
      this.itensAtuais = [];
      this.atualizarTabela();
      return;
    }

    this.resultado = { status: 'carregando' };
    this.atualizarTabela();

    const resultado = await api.listar(this.filtroApi());
    this.resultado = resultado;
    this.itensAtuais = resultado.status === 'ok' ? resultado.dado : [];
    this.atualizarTabela();
  }

  private async carregarResumo(): Promise<void> {
    let resumo: Resumo = { ...RESUMO_VAZIO };

    const dash = this._dashboardApi;
    if (dash) {
      try {
        const [status, avisos] = await Promise.all([dash.obterPedidosPorStatus(), dash.obterAvisos()]);
        if (status.status === 'ok') {
          const c = status.dado.contagem;
          resumo.totalPorStatus = c;
          resumo.totalPedidos = STATUS_PEDIDO.reduce((s, o) => s + c[o.valor], 0);
          resumo.aproximado = status.dado.aproximado;
          const amostra = status.dado.pedidos;
          resumo.amostra = amostra.length;
          resumo.pedidosCampanha = amostra.filter((p) => p.origem === 'campanha_pg').length;
          resumo.pedidosComCupom = amostra.filter((p) => p.cupomId !== null).length;
        }
        if (avisos.status === 'ok') {
          resumo.aguardandoAcao = avisos.dado
            .filter((a) => /pagamento|produção|técnico|OPS/i.test(a.titulo))
            .reduce((s, a) => s + a.quantidade, 0);
        }
      } catch {
        /* mantém RESUMO_VAZIO nesses campos */
      }
    }

    const rel = this._relatoriosApi;
    if (rel && this._permissoesRelatorios) {
      try {
        const [pulso, funil] = await Promise.all([rel.obterPulsoDoNegocio(), rel.obterFunilProducao()]);
        if (pulso.status === 'ok') {
          resumo.faturamentoPagoCentavos = pulso.dado.receitaMesCentavos;
          resumo.ticketMedioCentavos = pulso.dado.pedidosPagosMes > 0 ? pulso.dado.ticketMedioMesCentavos : 0;
          resumo.pedidosPagosMes = pulso.dado.pedidosPagosMes;
        }
        if (funil.status === 'ok') {
          const f: FunilProducao = funil.dado;
          const etapas = Object.keys(f) as Array<keyof FunilProducao>;
          resumo.emProducao = etapas
            .filter((e) => e !== ETAPA_CONCLUSAO && e !== 'enviado')
            .reduce((s, e) => s + f[e], 0);
          resumo.aguardandoEnvio = f['enviado'] ?? 0;
        }
      } catch {
        /* sem relatórios: KPIs financeiros ficam "—" */
      }
    }

    // Ticket médio sem `relatorios:ler`: cai no cálculo da 1ª página de pagos (mesmo do Dashboard).
    if (resumo.ticketMedioCentavos === null && dash) {
      try {
        const t = await dash.obterTicketMedio();
        if (t.status === 'ok') resumo.ticketMedioCentavos = t.dado.centavos;
      } catch {
        /* mantém null */
      }
    }

    this.resumo = resumo;
    this.atualizarKpis();
    this.atualizarSidebox();
    this.atualizarTabsCounts();
  }

  // ---------------------------------------------------------------------
  // Fragmentos
  // ---------------------------------------------------------------------

  private kpisHtml(): string {
    const r = this.resumo;
    const v = (n: number | null, fmt: (x: number) => string) => (n === null ? '—' : fmt(String(n) === 'NaN' ? 0 : n));
    const num = (n: number | null) => v(n, (x) => String(x));
    const brl = (n: number | null) => v(n, (x) => formatBRL(centavosParaReais(x)));
    return `
      <div class="kpi"><div class="klabel">Pedidos no período</div><div class="kval">${num(r.totalPedidos)}${r.aproximado && r.totalPedidos !== null ? '+' : ''}</div><div class="kmeta">Todos os status</div></div>
      <div class="kpi"><div class="klabel">Faturamento pago</div><div class="kval">${brl(r.faturamentoPagoCentavos)}</div><div class="kmeta">Mês corrente · pagamentos confirmados</div></div>
      <div class="kpi"><div class="klabel">Ticket médio</div><div class="kval">${brl(r.ticketMedioCentavos)}</div><div class="kmeta">Somente pedidos pagos</div></div>
      <div class="kpi"><div class="klabel">Pedidos em produção</div><div class="kval">${num(r.emProducao)}</div><div class="kmeta">${r.aguardandoEnvio === null ? 'OPS ativas' : `${r.aguardandoEnvio} em "Enviado"`}</div></div>
      <div class="kpi"><div class="klabel">Campanhas</div><div class="kval">${num(r.pedidosCampanha)}</div><div class="kmeta">${r.amostra > 0 ? `de ${r.amostra} pedidos recentes` : 'Origem "Campanha PG"'}</div></div>
    `;
  }

  private tabsHtml(): string {
    const opcoes: Array<{ valor: StatusPedido | ''; rotulo: string }> = [
      { valor: '', rotulo: 'Todos' },
      ...STATUS_PEDIDO.map((o) => ({ valor: o.valor, rotulo: o.rotulo })),
    ];
    return opcoes
      .map((o) => {
        const ativo = this.filtros.status === o.valor ? ' active' : '';
        return `<button type="button" class="tab${ativo}" data-status="${o.valor}">${esc(o.rotulo)}<b data-count="${o.valor || 'todos'}"></b></button>`;
      })
      .join('');
  }

  private atualizarTabsCounts(): void {
    const c = this.resumo.totalPorStatus;
    const total = this.resumo.totalPedidos;
    const setar = (chave: string, valor: number | null) => {
      const el = this.root.querySelector(`.tab b[data-count="${chave}"]`);
      if (el) el.textContent = valor === null ? '' : String(valor) + (this.resumo.aproximado ? '+' : '');
    };
    setar('todos', total);
    for (const o of STATUS_PEDIDO) setar(o.valor, c ? c[o.valor] : null);
  }

  private sideboxHtml(): string {
    const r = this.resumo;
    const pct = (parte: number | null, todo: number) =>
      parte === null || todo === 0 ? '' : `${((parte / todo) * 100).toFixed(1).replace('.', ',')}% da amostra recente`;
    const brl = (n: number | null) => (n === null ? '—' : formatBRL(centavosParaReais(n)));
    const pagoRatio =
      r.totalPorStatus && r.totalPedidos && r.totalPedidos > 0
        ? Math.round((r.totalPorStatus.pago / r.totalPedidos) * 100)
        : 0;
    return `
      <div class="side-title">Inteligência dos pedidos</div>
      <div class="metric">
        <span class="small muted">Faturamento pago (mês)</span>
        <b>${brl(r.faturamentoPagoCentavos)}</b>
        <div class="bar"><i style="width:${Math.max(4, Math.min(100, pagoRatio))}%"></i></div>
        <span class="small muted">${r.totalPorStatus ? `${pagoRatio}% dos pedidos estão pagos` : 'requer permissão de relatórios'}</span>
      </div>
      <div class="metric">
        <span class="small muted">Origem · Campanha PG</span>
        <b>${r.pedidosCampanha === null ? '—' : `${r.pedidosCampanha} pedidos`}</b>
        <span class="small muted">${pct(r.pedidosCampanha, r.amostra)}</span>
      </div>
      <div class="metric">
        <span class="small muted">Com cupom</span>
        <b>${r.pedidosComCupom === null ? '—' : `${r.pedidosComCupom} pedidos`}</b>
        <span class="small muted">${pct(r.pedidosComCupom, r.amostra)}</span>
      </div>
      <div class="metric">
        <span class="small muted">Aguardando ação</span>
        <b>${r.aguardandoAcao === null ? '—' : `${r.aguardandoAcao} pedidos`}</b>
        <span class="small muted">Pagamento ou produção</span>
      </div>
      <div class="insight"><b class="gold">Origem preservada</b><span class="small">Campanha, Site e demais canais vêm da mesma fonte de verdade (\`comercial.pedidos.origem\`) na lista e no detalhe.</span></div>
      <div class="insight"><b>Rastreamento</b><span class="small">Pedidos enviados exibem transportadora, código e botão direto para os Correios no detalhe.</span></div>
    `;
  }

  private atualizarKpis(): void {
    const el = this.root.querySelector('#kpis');
    if (el) el.innerHTML = this.kpisHtml();
  }

  private atualizarSidebox(): void {
    const el = this.root.querySelector('#sidebox');
    if (el) el.innerHTML = this.sideboxHtml();
  }

  private atualizarTabela(): void {
    const alvo = this.root.querySelector('#tabela');
    if (!alvo) return;

    if (this.resultado.status !== 'ok') {
      alvo.innerHTML = `<div class="estado">${textoEstado(this.resultado)}</div>`;
    } else if (this.itensAtuais.length === 0) {
      alvo.innerHTML = `<div class="estado">Nenhum pedido para os filtros atuais.</div>`;
    } else {
      alvo.innerHTML = `
        <table>
          <thead><tr>
            <th>Pedido</th><th>Cliente</th><th>Status</th><th>Origem</th><th>Cupom</th>
            <th>Parceiro</th><th>Pagamento</th><th class="num">Total</th><th>Criado em</th><th></th>
          </tr></thead>
          <tbody>${this.itensAtuais.map((p) => this.linhaHtml(p)).join('')}</tbody>
        </table>`;
      this.root.querySelectorAll<HTMLElement>('tbody tr[data-id]').forEach((tr) => {
        const abrir = () => this.abrirDetalhe(tr.dataset['id']!);
        tr.addEventListener('click', abrir);
        tr.querySelector<HTMLButtonElement>('.rowaction')?.addEventListener('click', (e) => {
          e.stopPropagation();
          abrir();
        });
      });
    }

    const anterior = this.root.querySelector('#anterior');
    const proxima = this.root.querySelector('#proxima');
    const info = this.root.querySelector('#info-pagina');
    const cheia = this.resultado.status === 'ok' && this.itensAtuais.length === LIMITE_PAGINA;
    anterior?.toggleAttribute('disabled', this.offset === 0);
    proxima?.toggleAttribute('disabled', !cheia);
    if (info) {
      info.textContent =
        this.resultado.status === 'ok' && this.itensAtuais.length > 0
          ? `Mostrando ${this.offset + 1}–${this.offset + this.itensAtuais.length}${cheia ? ` de ${this.offset + this.itensAtuais.length}+` : ''} pedidos`
          : '';
    }
  }

  private linhaHtml(p: PedidoListagem): string {
    const idCurto = p.id.slice(0, 8);
    // Identificação COMERCIAL: NFPG#### depois do pagamento, "OPS PG Custom" antes. O UUID
    // curto vira só rótulo técnico secundário.
    const referencia = rotuloReferenciaPedido(p);
    const cliente = p.clienteNome ?? `Cliente ${idCurto}`;
    const email = p.clienteEmail ? mascararEmail(p.clienteEmail) : '';
    const [dia, hora] = formatarDataParte(p.criadoEm);
    const origemLabel = rotuloOrigemPedido(p.origem);
    const dotClasse = p.origem === 'campanha_pg' ? 'dot p' : 'dot';
    const cupom = p.cupomCodigo
      ? `<span class="coupon">${esc(p.cupomCodigo)}</span>${p.cupomDescontoPercentual !== null ? `<div class="small">−${p.cupomDescontoPercentual}%</div>` : ''}`
      : '<span class="muted">—</span>';
    const parceiro = p.parceiroId
      ? `<span class="partner">${p.parceiroNome ? esc(p.parceiroNome) : 'Sim'}</span>${p.comissaoParceiroCentavos !== null ? `<div class="small gold-text">${formatBRL(centavosParaReais(p.comissaoParceiroCentavos))}</div>` : ''}`
      : '<span class="muted">—</span>';
    const pagto = p.gatewayPagamento
      ? `${esc(ROTULO_GATEWAY[p.gatewayPagamento] ?? p.gatewayPagamento)}<div class="small">${p.pagoEm ? formatarDataParte(p.pagoEm).join(' · ') : rotuloStatusPedido(p.status)}</div>`
      : `<span class="muted">—</span><div class="small">${rotuloStatusPedido(p.status)}</div>`;
    return `
      <tr data-id="${esc(p.id)}">
        <td><div class="order">${esc(referencia)}</div><div class="small">${esc(idCurto)}</div></td>
        <td><div class="customer">${esc(cliente)}</div>${email ? `<div class="small">${esc(email)}</div>` : ''}</td>
        <td><span class="badge ${TOM_STATUS_PEDIDO[p.status]}">${esc(rotuloStatusPedido(p.status))}</span></td>
        <td><div class="origin"><i class="${dotClasse}"></i>${esc(origemLabel)}</div></td>
        <td>${cupom}</td>
        <td>${parceiro}</td>
        <td>${pagto}</td>
        <td class="num money">${formatBRL(centavosParaReais(p.valorTotalCentavos))}</td>
        <td>${esc(dia)}<div class="small">${esc(hora)}</div></td>
        <td><button type="button" class="rowaction">Ver</button></td>
      </tr>`;
  }
}

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------

function textoEstado(resultado: Resultado<unknown>): string {
  switch (resultado.status) {
    case 'carregando':
      return 'Carregando pedidos…';
    case 'sem-permissao':
      return 'Você não tem permissão para ver os pedidos.';
    case 'erro':
      return esc(resultado.mensagem);
    default:
      return 'Nenhum pedido encontrado.';
  }
}

function formatarDataParte(iso: string): [string, string] {
  const d = new Date(iso);
  const dia = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(d);
  const hora = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(d);
  return [dia, hora];
}

function mascararEmail(email: string): string {
  const [usuario, dominio] = email.split('@');
  if (!dominio || !usuario) return email;
  const visivel = usuario.slice(0, Math.min(3, usuario.length));
  return `${visivel}${usuario.length > 3 ? '…' : ''}@${dominio}`;
}

function esc(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

const ESTILO = `
  :host { display: block; font-family: var(--pg-font-familia-base, system-ui); color: var(--pg-color-texto-primario, #f2f3f5); }
  .wrap { max-width: 1600px; margin: 0 auto; }

  .eyebrow { font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--pg-color-rosa-pg-art, #e05f9b); font-weight: 800; }
  h1 { font-family: var(--pg-font-familia-display, system-ui); font-size: 1.7rem; margin: 6px 0 4px; }
  .sub { color: var(--pg-color-texto-secundario, #9aa1ab); font-size: 0.875rem; margin: 0 0 22px; }
  .muted { color: var(--pg-color-texto-secundario, #9aa1ab); }
  .small { font-size: 11px; color: var(--pg-color-texto-desabilitado, #5c626b); margin-top: 3px; }
  .gold { color: var(--pg-color-dourado, #c9a24b); }
  .gold-text { color: var(--pg-color-dourado, #c9a24b); }

  .kpis { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 12px; }
  .kpi {
    background: linear-gradient(145deg, var(--pg-color-bg-elevado, #1a1d21), var(--pg-color-bg-base, #121417));
    border: 1px solid var(--pg-color-borda, #2a2f36); border-radius: 12px; padding: 16px;
    position: relative; overflow: hidden; min-width: 0;
  }
  .kpi:after { content: ""; position: absolute; right: -24px; top: -28px; width: 90px; height: 90px; border-radius: 50%; background: rgba(224, 95, 155, 0.12); }
  .klabel { font-size: 10px; color: var(--pg-color-texto-secundario, #9aa1ab); text-transform: uppercase; letter-spacing: 0.08em; }
  .kval { font-size: 22px; font-weight: 800; margin-top: 10px; font-variant-numeric: tabular-nums; word-break: break-word; }
  .kmeta { font-size: 11px; color: var(--pg-color-texto-desabilitado, #5c626b); margin-top: 5px; }

  .toolbar {
    margin-top: 18px; background: var(--pg-color-bg-elevado, #1a1d21); border: 1px solid var(--pg-color-borda, #2a2f36);
    border-radius: 12px; padding: 13px; display: grid; grid-template-columns: minmax(0, 1fr) repeat(4, 148px); gap: 9px;
  }
  .field { min-width: 0; }

  .tabs { display: flex; gap: 7px; flex-wrap: wrap; margin-top: 12px; }
  .tab {
    background: var(--pg-color-bg-elevado, #1a1d21); border: 1px solid var(--pg-color-borda, #2a2f36);
    padding: 8px 12px; border-radius: 999px; color: var(--pg-color-texto-secundario, #9aa1ab); font-size: 12px;
    display: inline-flex; align-items: center; gap: 6px;
  }
  .tab b { font-weight: 800; color: var(--pg-color-texto-primario, #f2f3f5); }
  .tab.active { background: rgba(224, 95, 155, 0.14); border-color: rgba(224, 95, 155, 0.4); color: var(--pg-color-texto-primario, #f2f3f5); }
  .tab:focus-visible { outline: none; box-shadow: var(--pg-shadow-foco, 0 0 0 3px rgba(47,140,255,0.35)); }

  .layout { display: grid; grid-template-columns: minmax(0, 1fr) 288px; gap: 14px; margin-top: 14px; align-items: start; }
  .tablebox, .sidebox {
    background: var(--pg-color-bg-elevado, #1a1d21); border: 1px solid var(--pg-color-borda, #2a2f36);
    border-radius: 12px; overflow: hidden;
  }
  .tablehead { padding: 14px 16px; border-bottom: 1px solid var(--pg-color-borda, #2a2f36); }
  .tablehead strong { font-size: 14px; }
  .tablewrap { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; min-width: 1060px; }
  th {
    text-align: left; color: var(--pg-color-texto-desabilitado, #5c626b); font-size: 10px; text-transform: uppercase;
    letter-spacing: 0.07em; padding: 12px 14px; background: var(--pg-color-bg-base, #121417); white-space: nowrap; font-weight: 700;
  }
  th.num, td.num { text-align: right; }
  td { padding: 12px 14px; border-top: 1px solid var(--pg-color-borda, #2a2f36); white-space: nowrap; font-size: 12.5px; vertical-align: top; }
  tbody tr { cursor: pointer; }
  tbody tr:hover { background: var(--pg-color-bg-hover, #22262b); }
  .order { font-weight: 750; }
  .customer { color: var(--pg-color-texto-primario, #f2f3f5); }
  .money { font-weight: 800; font-variant-numeric: tabular-nums; }
  .origin { display: flex; align-items: center; gap: 7px; }
  .dot { width: 7px; height: 7px; border-radius: 50%; background: var(--pg-color-dourado, #c9a24b); flex-shrink: 0; }
  .dot.p { background: var(--pg-color-rosa-pg-art, #e05f9b); }
  .coupon { color: var(--pg-color-dourado, #c9a24b); font-weight: 700; }
  .partner { color: var(--pg-color-roxo, #8b5cf6); font-weight: 700; }
  .badge {
    display: inline-flex; padding: 4px 8px; border-radius: 999px; font-size: 10px; font-weight: 800;
    text-transform: uppercase; letter-spacing: 0.04em;
  }
  .badge.pago { background: rgba(59, 178, 115, 0.16); color: var(--pg-color-sucesso, #3bb273); }
  .badge.pendente { background: rgba(224, 165, 44, 0.16); color: var(--pg-color-alerta, #e0a52c); }
  .badge.cancelado { background: rgba(229, 72, 77, 0.16); color: var(--pg-color-erro, #e5484d); }
  .rowaction {
    border: 1px solid var(--pg-color-borda, #2a2f36); background: var(--pg-color-bg-base, #121417);
    border-radius: 7px; padding: 6px 10px; color: var(--pg-color-texto-primario, #f2f3f5); font-size: 12px;
  }
  .rowaction:hover { border-color: var(--pg-color-dourado, #c9a24b); }
  .estado { padding: 40px 16px; text-align: center; color: var(--pg-color-texto-secundario, #9aa1ab); font-size: 13px; }

  .footer { padding: 12px 16px; border-top: 1px solid var(--pg-color-borda, #2a2f36); display: flex; align-items: center; justify-content: space-between; }
  .footer .small { margin: 0; }
  .pager { display: flex; gap: 6px; }
  .pgbtn {
    border: 1px solid var(--pg-color-borda, #2a2f36); background: var(--pg-color-bg-base, #121417);
    border-radius: 7px; padding: 7px 11px; color: var(--pg-color-texto-primario, #f2f3f5); font-size: 12px;
  }
  .pgbtn:hover:not([disabled]) { border-color: var(--pg-color-dourado, #c9a24b); }
  .pgbtn[disabled] { opacity: 0.4; cursor: default; }

  .sidebox { padding: 16px; }
  .side-title { font-weight: 800; margin-bottom: 14px; }
  .metric { padding: 12px 0; border-top: 1px solid var(--pg-color-borda, #2a2f36); }
  .metric:first-of-type { border-top: 0; padding-top: 0; }
  .metric b { display: block; font-size: 19px; margin: 4px 0; font-variant-numeric: tabular-nums; }
  .bar { height: 6px; background: var(--pg-color-bg-base, #121417); border-radius: 10px; overflow: hidden; margin: 8px 0; }
  .bar i { display: block; height: 100%; background: linear-gradient(90deg, var(--pg-color-rosa-pg-art, #e05f9b), var(--pg-color-dourado, #c9a24b)); border-radius: 10px; }
  .insight { background: var(--pg-color-bg-base, #121417); border: 1px solid var(--pg-color-borda, #2a2f36); border-radius: 9px; padding: 11px; margin-top: 10px; }
  .insight b { display: block; margin-bottom: 4px; font-size: 12px; }
  .insight .small { margin-top: 0; line-height: 1.5; }

  @media (max-width: 1240px) {
    .toolbar { grid-template-columns: minmax(0, 1fr) repeat(2, 1fr); }
    .kpis { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  }
  @media (max-width: 1000px) {
    .layout { grid-template-columns: 1fr; }
  }
  @media (max-width: 640px) {
    .kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .toolbar { grid-template-columns: 1fr; }
    h1 { font-size: 1.4rem; }
  }
`;

customElements.define('erp-pedidos-page', ErpPedidosPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-pedidos-page': ErpPedidosPage;
  }
}
